/**
 * ONE TOKENIZATION APPLICATION: where it is, and the one decision that is the owner's to make.
 *
 * FR-U-TOK-002 asks for exactly one thing - "track the asset verification and issuance status" - and
 * the honest answer to "where is my asset?" is not a status string. It is:
 *
 *   the STAGE it is in, out of how many          (the tracker)
 *   what is happening right now, and by whom     (the summary line)
 *   what is still needed, if anything            (the checklist, and the information request)
 *   what it was valued at, and by whom           (the valuation, when there is one)
 *   the offer, and WHICH of the three approvals are in
 *   every move anybody has made, in order        (the history)
 *
 * ## The offer is the reason this screen exists
 *
 * Fractionalizing somebody's property is a thing done TO an owner's asset, on terms. This is where
 * they read those terms and agree to them or do not, and the approval carries their own participant
 * id - the database refuses one that does not (`enforce_offer_approvals`, migration 0055). So the
 * button here is not a UI affordance over an officer's action: it IS the consent record.
 *
 * Declining is not rejecting tokenization, and the screen says so. The application goes back for the
 * terms to be reworked, because throwing away the diligence and the valuation over a lock-up period
 * would punish the owner for negotiating.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { Reveal } from '@/components/motion';
import { EmptyState, ErrorState, LoadingCard } from '@/components/states';
import { StatusPill } from '@/components/status';
import { DocumentSlot, useUploadPolicy } from '@/components/upload';
import { api, LipaApiError, type TokenizationApplication, type TokenizationOffer } from '@/lib/api';
import {
  ACTOR_LABEL,
  docLabel,
  formatMinor,
  formatUnits,
  STATUS_SUMMARY,
  statusTone,
} from '@/lib/tokenization';

export default function ApplicationRecord() {
  const { applicationId = '' } = useParams();
  const policy = useUploadPolicy();

  const [app, setApp] = useState<TokenizationApplication | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setApp(await api.tokenization.application(applicationId));
      setError(null);
    } catch (e) {
      setError(
        e instanceof LipaApiError ? e.detail.message : 'We could not load this application.',
      );
    }
  }, [applicationId]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Poll while it is moving.
   *
   * Not a websocket, and not a one-shot load either. An application changes hands between four
   * parties over days, and a screen that only ever showed the state at page load would tell somebody
   * their asset is "with the platform" for as long as they left the tab open. Polling stops once the
   * application reaches a state only the owner can move.
   */
  useEffect(() => {
    if (!app) return;
    const settled: string[] = [
      'draft',
      'information_requested',
      'offer_pending_user_approval',
      'tokens_issued',
      'listed_on_secondary_market',
      'completed',
      'rejected',
      'withdrawn',
    ];
    if (settled.includes(app.status)) return;

    const timer = setInterval(() => void load(), 4000);
    return () => clearInterval(timer);
  }, [app, load]);

  if (error) return <ErrorState body={error} onRetry={() => void load()} />;
  if (!app) return <LoadingCard lines={6} />;

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <Link
        to="/tokenize"
        className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-ink"
      >
        <BackIcon /> Tokenize
      </Link>

      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="truncate text-page-title text-ink">
            {app.details['description'] || app.asset.ownership_reference}
          </h1>
          <p className="mt-0.5 text-micro text-ink-subtle">
            <span className="font-mono">{app.reference}</span> &middot; {app.product.name}
            {app.details['location'] ? ` · ${app.details['location']}` : ''}
          </p>
        </div>
        <StatusPill status={statusTone(app.status)} label={label(app.status)} />
      </header>

      {/* WHERE IT IS. The one sentence somebody opens this screen for. */}
      <section className="rounded-card border border-primary/20 bg-primary-tint p-4">
        <p className="text-sm leading-relaxed text-ink">{STATUS_SUMMARY[app.status]}</p>
      </section>

      <Tracker stages={app.stages} />

      {/* THE OWNER'S DECISION. Above everything else when it is theirs to make. */}
      {app.offer && app.offer.status === 'pending_user' ? (
        <OfferCard offer={app.offer} applicationId={app.application_id} onDecided={load} />
      ) : null}

      {app.information_requested ? (
        <InformationRequest
          application={app}
          accept={policy?.accept ?? []}
          maxBytes={policy?.max_bytes ?? 0}
          onChanged={load}
        />
      ) : null}

      {app.rejection_reason ? (
        <section className="rounded-card border border-danger/30 bg-danger/5 p-4">
          <p className="text-sm font-medium text-ink">This application was not taken forward</p>
          <p className="mt-1 text-sm text-ink-muted">{app.rejection_reason}</p>
        </section>
      ) : null}

      {app.offer && app.offer.status !== 'pending_user' ? (
        <ApprovalsCard offer={app.offer} />
      ) : null}

      {app.token ? <IssuedTokenCard app={app} /> : null}

      {app.valuation ? <ValuationCard valuation={app.valuation} /> : null}

      <DocumentsCard app={app} />

      {app.asset.legal_entity ? (
        <section className="rounded-card border border-border bg-surface p-4 shadow-card">
          <h2 className="text-section text-ink">Who holds it</h2>
          <p className="mt-1 text-sm text-ink-muted">
            A token is an interest in the entity that holds the property, not in the property
            itself. Ownership of registered land is decided by the register, and a token cannot move
            the register.
          </p>
          <dl className="mt-3 divide-y divide-border rounded-card border border-border">
            <Row k="Holding entity" v={app.asset.legal_entity.name} />
            <Row k="Registration" v={app.asset.legal_entity.registration_number} />
            <Row k={referenceLabel(app.product.asset_class)} v={app.asset.ownership_reference} />
          </dl>
        </section>
      ) : null}

      <HistoryCard app={app} />
    </div>
  );
}

// ---- The tracker ---------------------------------------------------------------------------------

/**
 * The progress tracker. Nine stages, and every one of them comes off the wire.
 *
 * The UI does not decide which stage an application is in. `application.stages` is computed by the
 * backend from `LIFECYCLE_STAGES`, so a status the frontend has never heard of still lands in the
 * right row - and a UI that computed this itself would be a second state machine, disagreeing with
 * the first one on the day somebody adds a status.
 */
function Tracker({ stages }: { stages: TokenizationApplication['stages'] }) {
  const done = stages.filter((s) => s.state === 'done').length;

  return (
    <section className="rounded-card border border-border bg-surface p-4 shadow-card">
      <div className="flex items-baseline justify-between">
        <h2 className="text-section text-ink">Progress</h2>
        <p className="tabular text-micro text-ink-subtle">
          {done} of {stages.length} complete
        </p>
      </div>

      <ol className="mt-3 space-y-0">
        {stages.map((stage, i) => (
          <li key={stage.label} className="flex gap-3">
            <div className="flex flex-col items-center">
              <span
                className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-nano ${
                  stage.state === 'done'
                    ? 'border-accent bg-accent text-white'
                    : stage.state === 'current'
                      ? 'border-primary bg-primary text-white'
                      : 'border-border bg-surface text-ink-subtle'
                }`}
                aria-hidden
              >
                {stage.state === 'done' ? <TickIcon /> : i + 1}
              </span>
              {i < stages.length - 1 ? (
                <span
                  className={`w-px flex-1 ${stage.state === 'done' ? 'bg-accent/40' : 'bg-border'}`}
                />
              ) : null}
            </div>
            <div className="min-w-0 pb-3">
              <p
                className={`text-sm ${
                  stage.state === 'current'
                    ? 'font-semibold text-ink'
                    : stage.state === 'done'
                      ? 'text-ink-muted'
                      : 'text-ink-subtle'
                }`}
              >
                {stage.label}
              </p>
              {stage.state === 'current' ? (
                <p className="text-micro text-ink-subtle">Happening now</p>
              ) : null}
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

// ---- The offer -----------------------------------------------------------------------------------

function OfferCard({
  offer,
  applicationId,
  onDecided,
}: {
  offer: TokenizationOffer;
  applicationId: string;
  onDecided: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function decide(accept: boolean) {
    setBusy(true);
    setError(null);
    try {
      await api.tokenization.decideOffer(applicationId, accept, accept ? undefined : reason);
      await onDecided();
    } catch (e) {
      setError(e instanceof LipaApiError ? e.detail.message : 'We could not record your decision.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Reveal>
      <section className="rounded-card border-2 border-primary bg-surface p-5 shadow-soft">
        <h2 className="text-section text-ink">Your tokenization offer</h2>
        <p className="mt-1 text-sm text-ink-muted">
          Read this carefully. Agreeing to it is what permits your asset to be tokenized, and
          nothing is issued before you do.
        </p>

        <dl className="mt-4 divide-y divide-border rounded-card border border-border">
          <Row k="Token name" v={offer.token.name} />
          <Row k="Symbol" v={offer.token.symbol} />
          <Row k="Tokens issued" v={offer.token.quantity.toLocaleString('en-KE')} />
          <Row
            k="Value per token"
            v={`${offer.token.asset_code} ${formatMinor(offer.token.unit_value, offer.token.asset_code)}`}
          />
          <Row
            k="Total tokenized value"
            v={`${offer.token.asset_code} ${formatMinor(offer.token.total_value, offer.token.asset_code)}`}
          />
          <Row
            k="Share of the asset"
            v={`${(offer.token.tokenized_bps / 100).toFixed(2)}% (you keep ${((10000 - offer.token.tokenized_bps) / 100).toFixed(2)}%)`}
          />
          <Row k="Classified as" v={offer.token.classification.replace('_', ' ')} />
          {Object.entries(offer.terms).map(([k, v]) => (
            <Row key={k} k={termLabel(k)} v={String(v)} />
          ))}
        </dl>

        <p className="mt-3 text-micro text-ink-subtle">
          Priced off the valuation of{' '}
          {offer.valuation.effective_from
            ? new Date(offer.valuation.effective_from).toLocaleDateString('en-KE')
            : 'the approved report'}{' '}
          by {offer.valuation.valuer}, on the {offer.valuation.method} approach, which the trust has
          approved.
        </p>

        {error ? (
          <p className="mt-3 rounded-field bg-danger/10 px-3 py-2 text-sm text-danger" role="alert">
            {error}
          </p>
        ) : null}

        {declining ? (
          <div className="mt-4">
            <label className="block">
              <span className="text-sm font-medium text-ink">What would you change?</span>
              <span className="mt-0.5 block text-micro text-ink-subtle">
                The terms go back to be reworked. Your valuation and diligence are not thrown away.
              </span>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                className="mt-1.5 w-full rounded-field border border-border bg-surface px-3 py-2.5 text-base text-ink outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
            </label>
            <div className="mt-3 flex gap-2.5">
              <button
                onClick={() => setDeclining(false)}
                className="flex-1 rounded-field border border-border py-2.5 text-sm font-medium text-ink transition hover:bg-surface-sunken"
              >
                Cancel
              </button>
              <button
                onClick={() => void decide(false)}
                disabled={busy || reason.trim().length < 3}
                className="flex-1 rounded-field bg-danger py-2.5 text-sm font-medium text-white transition disabled:opacity-40"
              >
                {busy ? 'Sending...' : 'Send it back'}
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-4 flex gap-2.5">
            <button
              onClick={() => setDeclining(true)}
              className="flex-1 rounded-field border border-border py-3 text-sm font-medium text-ink transition hover:bg-surface-sunken"
            >
              Decline these terms
            </button>
            <button
              onClick={() => void decide(true)}
              disabled={busy}
              className="flex-1 rounded-field bg-primary py-3 text-sm font-medium text-white transition hover:bg-primary-light disabled:opacity-60"
            >
              {busy ? 'Recording...' : 'Agree to these terms'}
            </button>
          </div>
        )}
      </section>
    </Reveal>
  );
}

/**
 * THE THREE APPROVALS, shown as three.
 *
 * Not one "approved" badge. An owner who has agreed is entitled to see that the trust has signed and
 * the platform has not, because those are two different things standing between them and their
 * tokens, and only one of them is anybody they can chase.
 */
function ApprovalsCard({ offer }: { offer: TokenizationOffer }) {
  const rows: [string, string | null, string][] = [
    ['The trust', offer.approvals.trust, 'Approved the valuation this is priced off'],
    ['You', offer.approvals.user, 'Agreed to the terms'],
    ['VASP', offer.approvals.platform, 'Approved the issuance'],
  ];

  return (
    <section className="rounded-card border border-border bg-surface p-4 shadow-card">
      <h2 className="text-section text-ink">Approvals</h2>
      <p className="mt-1 text-sm text-ink-muted">
        All three are needed before any token is issued.
      </p>
      <ul className="mt-3 space-y-2">
        {rows.map(([who, at, what]) => (
          <li key={who} className="flex items-start gap-3">
            <span
              className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
                at ? 'bg-accent text-white' : 'border border-border bg-surface text-ink-subtle'
              }`}
              aria-hidden
            >
              {at ? <TickIcon /> : null}
            </span>
            <div className="min-w-0">
              <p className="text-sm font-medium text-ink">{who}</p>
              <p className="text-micro text-ink-subtle">
                {what}
                {at ? ` · ${new Date(at).toLocaleDateString('en-KE')}` : ' · not yet'}
              </p>
            </div>
          </li>
        ))}
      </ul>

      {offer.status === 'rejected' && offer.rejection_reason ? (
        <p className="mt-3 rounded-field bg-surface-sunken px-3 py-2 text-micro text-ink-muted">
          Returned by {ACTOR_LABEL[offer.rejected_by ?? ''] ?? 'a reviewer'}:{' '}
          {offer.rejection_reason}
        </p>
      ) : null}
    </section>
  );
}

// ---- The issued token ----------------------------------------------------------------------------

function IssuedTokenCard({ app }: { app: TokenizationApplication }) {
  const token = app.token!;
  return (
    <section className="rounded-card border border-accent/30 bg-accent/5 p-4">
      <h2 className="text-section text-ink">Your tokens</h2>
      <dl className="mt-3 divide-y divide-border rounded-card border border-border bg-surface">
        <Row k="Token" v={`${token.name ?? '-'} (${token.symbol ?? '-'})`} />
        <Row k="Issued" v={formatUnits(token.total_supply)} />
        {token.unit_value ? (
          <Row
            k="Value per token"
            v={`${token.quote_asset ?? 'KES'} ${formatMinor(token.unit_value, token.quote_asset ?? 'KES')}`}
          />
        ) : null}
        <Row k="Underlying asset" v={app.details['description'] || app.asset.ownership_reference} />
        <Row k="Status" v={token.state === 'locked' ? 'Pledged as collateral' : 'Active'} />
      </dl>
      <div className="mt-3 flex gap-2.5">
        <Link
          to="/tokenize/portfolio"
          className="flex-1 rounded-field border border-border bg-surface py-2.5 text-center text-sm font-medium text-ink transition hover:bg-surface-sunken"
        >
          Portfolio
        </Link>
        <Link
          to={`/tokenize/tokens/${token.token_id}/list`}
          className="flex-1 rounded-field bg-primary py-2.5 text-center text-sm font-medium text-white transition hover:bg-primary-light"
        >
          List on the marketplace
        </Link>
      </div>
    </section>
  );
}

// ---- Valuation, documents, history ---------------------------------------------------------------

function ValuationCard({
  valuation,
}: {
  valuation: NonNullable<TokenizationApplication['valuation']>;
}) {
  return (
    <section className="rounded-card border border-border bg-surface p-4 shadow-card">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-section text-ink">Valuation</h2>
        <StatusPill
          status={valuation.status === 'accepted' ? 'verified' : 'in review'}
          label={valuation.status === 'accepted' ? 'Approved by the trust' : 'Under review'}
        />
      </div>

      <dl className="mt-3 divide-y divide-border rounded-card border border-border">
        <Row
          k="Assessed value"
          v={`${valuation.asset_code} ${formatMinor(valuation.adopted_value, valuation.asset_code)}`}
        />
        {/* The forced-sale figure is shown SEPARATELY and labelled, never collapsed into the value.
            A lender reading a forced-sale number as a market value is a documented way people lose
            money. */}
        {valuation.forced_sale_value ? (
          <Row
            k="Forced-sale value"
            v={`${valuation.asset_code} ${formatMinor(valuation.forced_sale_value, valuation.asset_code)}`}
          />
        ) : null}
        <Row k="Approach" v={`${valuation.method} · ${valuation.basis}`} />
        <Row
          k="Valuer"
          v={valuation.valuer + (valuation.valuer_licence ? ` (${valuation.valuer_licence})` : '')}
        />
        {valuation.effective_from ? (
          <Row k="As at" v={new Date(valuation.effective_from).toLocaleDateString('en-KE')} />
        ) : null}
      </dl>

      {valuation.risk_observations.length > 0 ? (
        <div className="mt-3">
          <p className="text-micro font-medium text-ink">What the valuer noted</p>
          <ul className="mt-1 space-y-1">
            {valuation.risk_observations.map((r, i) => (
              <li key={i} className="text-micro text-ink-muted">
                {r.detail}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}

function DocumentsCard({ app }: { app: TokenizationApplication }) {
  return (
    <section className="rounded-card border border-border bg-surface p-4 shadow-card">
      <h2 className="text-section text-ink">Documents</h2>
      {app.documents.length === 0 ? (
        <p className="mt-1 text-sm text-ink-subtle">Nothing uploaded yet.</p>
      ) : (
        <ul className="mt-3 space-y-2">
          {app.documents.map((d) => (
            <li
              key={d.document_id}
              className="flex items-center justify-between gap-3 rounded-field bg-surface-sunken px-3 py-2"
            >
              <div className="min-w-0">
                <p className="truncate text-micro font-medium text-ink">
                  {docLabel(d.doc_type)}
                  {d.file_name ? ` · ${d.file_name}` : ''}
                </p>
                {/* The hash the SERVER computed over the bytes. Shown because it is what makes this
                    evidence: an owner can check it against their own copy of the file. */}
                <p className="truncate font-mono text-nano text-ink-subtle">
                  {d.content_hash.slice(0, 24)}...
                </p>
                {d.status === 'rejected' && d.review_note ? (
                  <p className="text-nano text-danger">{d.review_note}</p>
                ) : null}
              </div>
              <StatusPill
                status={
                  d.status === 'accepted'
                    ? 'verified'
                    : d.status === 'rejected'
                      ? 'failed'
                      : 'pending'
                }
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function InformationRequest({
  application,
  accept,
  maxBytes,
  onChanged,
}: {
  application: TokenizationApplication;
  accept: string[];
  maxBytes: number;
  onChanged: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const outstanding = application.checklist.filter((c) => !c.satisfied);

  async function resubmit() {
    setBusy(true);
    setError(null);
    try {
      await api.tokenization.submit(application.application_id);
      await onChanged();
    } catch (e) {
      setError(e instanceof LipaApiError ? e.detail.message : 'We could not resubmit this.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="rounded-card border-2 border-warning bg-surface p-5 shadow-soft">
      <h2 className="text-section text-ink">We need something more</h2>
      <p className="mt-1 text-sm text-ink-muted">{application.information_requested}</p>

      {outstanding.length > 0 ? (
        <div className="mt-4 space-y-3">
          {outstanding.map((c) => (
            <DocumentSlot
              key={c.doc_type}
              applicationId={application.application_id}
              docType={c.doc_type}
              label={docLabel(c.doc_type)}
              note={c.note}
              required={c.required}
              minCount={c.min_count}
              documents={application.documents}
              accept={accept}
              maxBytes={maxBytes}
              onChanged={onChanged}
            />
          ))}
        </div>
      ) : null}

      {error ? (
        <p className="mt-3 rounded-field bg-danger/10 px-3 py-2 text-sm text-danger">{error}</p>
      ) : null}

      <button
        onClick={() => void resubmit()}
        disabled={busy}
        className="mt-4 w-full rounded-field bg-primary py-3 text-sm font-medium text-white transition hover:bg-primary-light disabled:opacity-50"
      >
        {busy ? 'Sending...' : 'Send it back for review'}
      </button>
      <p className="mt-2 text-center text-micro text-ink-subtle">
        It goes straight back to the reviewer who asked, not to the back of the queue.
      </p>
    </section>
  );
}

/**
 * THE AUDIT TRAIL, as the owner's own history.
 *
 * Read straight off `tokenization.application_transition`, which is append-only. Nothing summarises
 * it and nothing derives it: an owner asking what happened to their property gets the record itself,
 * with who did each thing and what role they held at the time.
 */
function HistoryCard({ app }: { app: TokenizationApplication }) {
  if (app.history.length === 0) {
    return <EmptyState title="Nothing has happened yet" body="Submit it and this fills up." />;
  }

  return (
    <section className="rounded-card border border-border bg-surface p-4 shadow-card">
      <h2 className="text-section text-ink">History</h2>
      <ol className="mt-3 space-y-3">
        {[...app.history].reverse().map((h, i) => (
          <li key={i} className="flex gap-3">
            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden />
            <div className="min-w-0">
              <p className="text-sm text-ink">{h.action}</p>
              <p className="text-micro text-ink-subtle">
                {ACTOR_LABEL[h.actor] ?? h.actor}
                {h.actor_role ? ` (${h.actor_role.replace(/_/g, ' ')})` : ''} &middot;{' '}
                {new Date(h.at).toLocaleString('en-KE')}
              </p>
              {h.note ? <p className="mt-0.5 text-micro text-ink-muted">{h.note}</p> : null}
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

// ---- Bits ----------------------------------------------------------------------------------------

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-start justify-between gap-3 px-4 py-2.5">
      <dt className="text-sm text-ink-muted">{k}</dt>
      <dd className="tabular max-w-[60%] text-right text-sm font-medium text-ink">{v}</dd>
    </div>
  );
}

function label(status: string): string {
  return status.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

function termLabel(key: string): string {
  return (
    { income: 'Income sharing', lockup_months: 'Lock-up (months)', voting: 'Investor voting' }[
      key
    ] ?? key.replace(/_/g, ' ')
  );
}

function referenceLabel(assetClass: string): string {
  return (
    {
      real_estate: 'Title number',
      land: 'Title number',
      vehicle: 'Registration',
      receivable: 'Contract reference',
      inventory: 'Warehouse receipt',
    }[assetClass] ?? 'Reference'
  );
}

function TickIcon() {
  return (
    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="m5 13 4 4L19 7" />
    </svg>
  );
}

function BackIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 6l-6 6 6 6" />
    </svg>
  );
}
